import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, ArrowRight, CheckCircle, Clock, Shield, MapPin, AlertTriangle,
  Zap, Award, ThumbsUp, Star, Wrench
} from 'lucide-react';
import { companyInfo, cities, testimonials } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { FAQSection, InternalLinksSection, faqVitrerieVilliers } from '../seo/FAQSection';
import SEO from '../seo/SEO';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

const VitrerieLocalPage = () => {
  // SEO Meta optimisé pour 1ère page Google
  const pageTitle = "Vitrier Villiers-sur-Marne (94350) | Dépannage 24h/24";
  const pageDescription = "Vitrier à Villiers-sur-Marne 24h/24 – remplacement de vitre, bris de glace, double vitrage. Intervention rapide dans le 94. Devis gratuit ☎ 07 81 55 90 50";
  const pageKeywords = "vitrier villiers-sur-marne, vitrier 94350, bris de glace villiers-sur-marne, remplacement vitre 94, dépannage vitrerie val-de-marne, double vitrage villiers-sur-marne, vitrier urgence 94";
  const canonical = "https://www.menuisiervitrier.fr/vitrier-villiers-sur-marne";

  // Images optimisées SEO avec ALT text
  const images = {
    hero: {
      src: "https://customer-assets.emergentagent.com/job_2a7b134a-4bfa-42f2-939f-0f0ad5675c3e/artifacts/yd0jksi9_Vitrier-villiers-sur-marne-94-val-de-marne0781559050.jpg",
      alt: "Vitrier à Villiers-sur-Marne – intervention urgence bris de glace 94350"
    },
    vitrerie: {
      src: "https://customer-assets.emergentagent.com/job_2a7b134a-4bfa-42f2-939f-0f0ad5675c3e/artifacts/0d6i10ch_Vitrier-villiers-sur-marne-94-val-de-marne.jpg",
      alt: "Remplacement de vitre cassée à Villiers-sur-Marne Val-de-Marne"
    },
    fenetre: {
      src: "https://customer-assets.emergentagent.com/job_2a7b134a-4bfa-42f2-939f-0f0ad5675c3e/artifacts/absqi9sx_Vitrier-villiers-sur-marne-94-val-de-marne-fenetre-pvc.jpg",
      alt: "Installation fenêtre PVC double vitrage Villiers-sur-Marne 94"
    },
    ngl: {
      src: "https://customer-assets.emergentagent.com/job_2a7b134a-4bfa-42f2-939f-0f0ad5675c3e/artifacts/hpp1e45j_Vitrier-villiers-sur-marne-94-val-de-marne-ngl-services.jpg",
      alt: "NGL Services vitrier professionnel Villiers-sur-Marne dépannage vitrerie"
    }
  };

  const vitrerieServices = [
    {
      title: "Remplacement de vitre cassée",
      description: "Intervention urgence pour vitre cassée à Villiers-sur-Marne. Simple vitrage, double vitrage, vitrage feuilleté.",
      features: ["Intervention en -1h", "Tous types de vitrage", "Mesures sur place"]
    },
    {
      title: "Bris de glace & urgences",
      description: "Réparation bris de glace 24h/24 à Villiers-sur-Marne (94350). Sécurisation immédiate de votre habitation.",
      features: ["Urgence 24h/24", "Sécurisation immédiate", "Remplacement rapide"]
    },
    {
      title: "Double vitrage isolation",
      description: "Installation et remplacement double vitrage isolant. Amélioration thermique et phonique de votre logement.",
      features: ["Isolation thermique", "Isolation phonique", "Économies d'énergie"]
    },
    {
      title: "Baie vitrée & grands formats",
      description: "Pose et réparation baie vitrée à Villiers-sur-Marne. Intervention pour tous types de baies coulissantes.",
      features: ["Grands formats", "Coulissantes", "À galandage"]
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO title={pageTitle} description={pageDescription} canonical="/vitrier-villiers-sur-marne" />
      {/* SEO Head optimisé */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={images.hero.src} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="fr_FR" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="FR-94" />
        <meta name="geo.placename" content="Villiers-sur-Marne" />
        <meta name="geo.position" content="48.8273;2.5449" />
        
        {/* Schema.org LocalBusiness */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "NGL SERVICES - Vitrier Villiers-sur-Marne",
            "image": "${images.ngl.src}",
            "description": "Vitrier à Villiers-sur-Marne (94350). Dépannage vitrerie urgence 24h/24, remplacement vitre cassée, bris de glace, double vitrage.",
            "url": "${canonical}",
            "telephone": "+33781559050",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Villiers-sur-Marne",
              "postalCode": "94350",
              "addressRegion": "Val-de-Marne",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 48.8273,
              "longitude": 2.5449
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "00:00",
              "closes": "23:59"
            },
            "priceRange": "€€",
            "areaServed": ["Villiers-sur-Marne", "Champigny-sur-Marne", "Bry-sur-Marne", "Nogent-sur-Marne", "Créteil"]
          }
        `}</script>
      </Helmet>

      {/* Hero Section avec image optimisée */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.hero.src})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(215,25%,15%)]/90 to-[hsl(215,25%,15%)]/70" />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumb SEO */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-white/70 hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/services/vitrerie" className="text-white/70 hover:text-white">Vitrerie</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Villiers-sur-Marne</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Badge className="badge-emergency mb-4">
            <Clock className="w-3 h-3 mr-1" /> Urgence Vitrier 24h/24 – 7j/7
          </Badge>
          
          {/* H1 unique et optimisé */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Vitrier à <span className="text-[hsl(24,95%,53%)]">Villiers-sur-Marne</span> (94350)
          </h1>
          
          <p className="text-xl text-gray-200 mb-4">
            <strong>Dépannage vitrerie • Bris de glace • Double vitrage • Val-de-Marne (94)</strong>
          </p>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl">
            NGL SERVICES, votre vitrier de confiance à Villiers-sur-Marne. Intervention urgence 
            en moins d'1 heure pour remplacement de vitre cassée, réparation bris de glace et 
            installation double vitrage. Disponible 24h/24 dans tout le Val-de-Marne.
          </p>

          <div className="flex flex-wrap gap-4 mb-8">
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="emergency-btn rounded-full text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                Urgence : {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg px-8">
                Devis gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-6 text-white/90">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Intervention -1h</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Devis gratuit</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[hsl(24,95%,53%)]" />
              <span>Garantie travaux</span>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau urgence */}
      <section className="py-6 bg-[hsl(24,95%,53%)] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <AlertTriangle className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">Vitre cassée ? Bris de glace à Villiers-sur-Marne ?</p>
                <p className="text-white/90">Vitrier urgence 24h/24 dans le Val-de-Marne (94)</p>
              </div>
            </div>
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO PRINCIPALE - H2 #1 ===== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Vitrier à Villiers-sur-Marne – Intervention rapide 24h/24
              </h2>
              
              {/* Paragraphe 1 */}
              <p className="text-muted-foreground mb-4">
                <strong>NGL SERVICES</strong> est votre <strong>vitrier à Villiers-sur-Marne</strong> (94350) 
                depuis plusieurs années. Notre équipe de vitriers professionnels intervient en urgence 
                pour tout <strong>remplacement de vitre cassée</strong>, <strong>bris de glace</strong> 
                et installation de <strong>double vitrage</strong> dans tout le secteur de Villiers-sur-Marne 
                et les communes environnantes du <strong>Val-de-Marne</strong>.
              </p>
              
              {/* Paragraphe 2 */}
              <p className="text-muted-foreground mb-4">
                Que vous soyez confronté à une <strong>vitre cassée suite à un cambriolage</strong>, 
                un <strong>bris de glace accidentel</strong>, ou que vous souhaitiez améliorer 
                l'isolation de votre logement avec un <strong>double vitrage performant</strong>, 
                nos vitriers qualifiés sont disponibles <strong>24h/24 et 7j/7</strong> pour 
                répondre à toutes vos urgences en vitrerie.
              </p>

              {/* H3 #1 */}
              <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mt-6 mb-3">
                Pourquoi choisir NGL SERVICES pour votre vitrerie ?
              </h3>
              
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[hsl(207,78%,42%)] flex-shrink-0 mt-0.5" />
                  <span><strong>Intervention rapide</strong> : vitrier sur place en moins d'1 heure à Villiers-sur-Marne</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[hsl(207,78%,42%)] flex-shrink-0 mt-0.5" />
                  <span><strong>Disponibilité 24h/24</strong> : dépannage vitrerie disponible jour et nuit, 7j/7</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[hsl(207,78%,42%)] flex-shrink-0 mt-0.5" />
                  <span><strong>Devis gratuit</strong> : estimation transparente avant toute intervention</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[hsl(207,78%,42%)] flex-shrink-0 mt-0.5" />
                  <span><strong>Vitrier qualifié</strong> : artisan expérimenté pour tous types de vitrage</span>
                </li>
              </ul>
            </div>
            
            {/* Image SEO optimisée */}
            <div className="relative">
              <img 
                src={images.vitrerie.src}
                alt={images.vitrerie.alt}
                className="rounded-2xl shadow-xl w-full"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[hsl(207,78%,95%)] rounded-full flex items-center justify-center">
                    <Zap className="w-6 h-6 text-[hsl(207,78%,42%)]" />
                  </div>
                  <div>
                    <p className="font-bold text-[hsl(215,25%,15%)]">Intervention -1h</p>
                    <p className="text-sm text-muted-foreground">Villiers-sur-Marne</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO - H2 #2 ===== */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6 text-center">
            Travaux de vitrerie et remplacement de vitre dans le Val-de-Marne (94)
          </h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-12">
            Notre entreprise de vitrerie à Villiers-sur-Marne propose un service complet de 
            dépannage et d'installation pour particuliers et professionnels dans tout le département 94.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {vitrerieServices.map((service, index) => (
              <Card key={index} className="card-service hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[hsl(207,78%,95%)] flex items-center justify-center mb-4">
                    <Wrench className="w-6 h-6 text-[hsl(207,78%,42%)]" />
                  </div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">{service.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
                  <ul className="space-y-1">
                    {service.features.map((feature, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-[hsl(207,78%,42%)]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO - H2 #3 ===== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image SEO */}
            <div className="order-2 lg:order-1">
              <img 
                src={images.fenetre.src}
                alt={images.fenetre.alt}
                className="rounded-2xl shadow-xl w-full"
                loading="lazy"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Urgence vitrier – Bris de glace à Villiers-sur-Marne
              </h2>
              
              {/* Paragraphe 3 */}
              <p className="text-muted-foreground mb-4">
                En cas d'<strong>urgence vitrerie à Villiers-sur-Marne</strong>, notre équipe 
                de vitriers professionnels intervient rapidement pour <strong>sécuriser votre 
                habitation</strong>. Que ce soit suite à un cambriolage, un accident domestique 
                ou des intempéries, nous assurons le <strong>remplacement de votre vitre cassée</strong> 
                dans les meilleurs délais.
              </p>
              
              {/* Paragraphe 4 */}
              <p className="text-muted-foreground mb-6">
                Notre service de <strong>dépannage vitrerie</strong> couvre l'ensemble du 
                <strong> Val-de-Marne (94)</strong> : Villiers-sur-Marne, Champigny-sur-Marne, 
                Bry-sur-Marne, Nogent-sur-Marne, Le Perreux-sur-Marne, Saint-Maur-des-Fossés, 
                Créteil, Noisy-le-Grand. Nous intervenons pour les particuliers comme pour 
                les professionnels (vitrines de commerce, immeubles, copropriétés).
              </p>

              {/* H3 #2 */}
              <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-4">
                Nos prestations vitrerie à Villiers-sur-Marne
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Remplacement vitre simple",
                  "Installation double vitrage",
                  "Triple vitrage isolant",
                  "Vitrage feuilleté sécurité",
                  "Réparation baie vitrée",
                  "Vitrine commerce",
                  "Miroir sur mesure",
                  "Crédence de cuisine"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-[hsl(207,78%,42%)]" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link to="/contact">
                  <Button className="btn-primary rounded-full">
                    Demander un devis gratuit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO ENRICHIE - Texte complet 700+ mots ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Votre vitrier de confiance à Villiers-sur-Marne (94350)
            </h2>
            
            <p className="text-muted-foreground mb-4">
              Situé en plein cœur du <strong>Val-de-Marne</strong>, <strong>NGL SERVICES</strong> est 
              votre artisan vitrier de proximité pour tous vos besoins en <strong>vitrerie, miroiterie 
              et dépannage d'urgence</strong>. Nous intervenons rapidement sur <strong>Villiers-sur-Marne</strong> 
              et dans l'ensemble des communes environnantes du département <strong>94</strong>.
            </p>

            <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mt-8 mb-4">
              Des services de vitrerie complets à Villiers-sur-Marne
            </h3>
            
            <p className="text-muted-foreground mb-4">
              Que vous ayez besoin d'un <strong>remplacement de vitre cassée en urgence</strong>, 
              d'une <strong>pose de double vitrage isolant</strong>, ou d'une <strong>mise en sécurité 
              après effraction</strong>, notre équipe de vitriers qualifiés est à votre service 
              <strong>24 heures sur 24 et 7 jours sur 7</strong>. Nous proposons également l'installation 
              de <strong>vitrage feuilleté de sécurité</strong>, de <strong>vitrage thermique haute performance</strong>, 
              ainsi que la pose de <strong>miroirs sur mesure</strong> et la réparation de 
              <strong>vitrines de commerces</strong>.
            </p>

            <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mt-8 mb-4">
              Urgence vitrerie 24h/24 dans le Val-de-Marne
            </h3>
            
            <p className="text-muted-foreground mb-4">
              Un <strong>bris de glace</strong> peut survenir à tout moment : tentative d'effraction, 
              accident domestique, intempéries, vandalisme... C'est pourquoi <strong>NGL SERVICES</strong> 
              assure un <strong>service d'urgence vitrerie</strong> disponible de jour comme de nuit. 
              Notre vitrier intervient en <strong>moins d'une heure</strong> sur Villiers-sur-Marne 
              pour <strong>sécuriser votre habitation</strong> et procéder au <strong>remplacement 
              de la vitre endommagée</strong>.
            </p>

            <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mt-8 mb-4">
              Pourquoi faire confiance à NGL SERVICES ?
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4 my-6">
              <div className="p-4 bg-[hsl(210,20%,98%)] rounded-lg">
                <h4 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Réactivité et disponibilité</h4>
                <p className="text-sm text-muted-foreground">
                  Intervention rapide sur Villiers-sur-Marne et le Val-de-Marne. Service d'urgence 
                  24h/24 et 7j/7, y compris les jours fériés.
                </p>
              </div>
              <div className="p-4 bg-[hsl(210,20%,98%)] rounded-lg">
                <h4 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Artisan expérimenté</h4>
                <p className="text-sm text-muted-foreground">
                  Nos vitriers possèdent plusieurs années d'expérience dans la pose, la réparation 
                  et le remplacement de tout type de vitrage.
                </p>
              </div>
              <div className="p-4 bg-[hsl(210,20%,98%)] rounded-lg">
                <h4 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Transparence tarifaire</h4>
                <p className="text-sm text-muted-foreground">
                  Devis gratuit et détaillé avant toute intervention. Aucune mauvaise surprise, 
                  nos tarifs sont clairs et communiqués à l'avance.
                </p>
              </div>
              <div className="p-4 bg-[hsl(210,20%,98%)] rounded-lg">
                <h4 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Solutions adaptées</h4>
                <p className="text-sm text-muted-foreground">
                  Nous vous conseillons sur le type de vitrage le plus adapté à vos besoins : 
                  isolation, sécurité, esthétique, budget.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mt-8 mb-4">
              Paiement facilité et partenariat assurance
            </h3>
            
            <p className="text-muted-foreground mb-4">
              Parce qu'une vitre cassée représente souvent une dépense imprévue, <strong>NGL SERVICES</strong> 
              vous propose un <strong>paiement en plusieurs fois</strong> pour vous permettre de 
              réaliser vos travaux de vitrerie sans impacter votre budget. De plus, en tant que 
              <strong>vitrier agréé assurance</strong>, nous pouvons vous accompagner dans vos 
              démarches de <strong>déclaration de sinistre</strong> et faciliter la prise en charge 
              de vos réparations par votre assureur.
            </p>

            <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mt-8 mb-4">
              Zone d'intervention vitrier Val-de-Marne
            </h3>
            
            <p className="text-muted-foreground mb-4">
              Au-delà de <strong>Villiers-sur-Marne</strong>, notre entreprise de vitrerie intervient 
              dans tout le <strong>Val-de-Marne (94)</strong> et les départements limitrophes. 
              Nos vitriers se déplacent notamment à <strong>Champigny-sur-Marne</strong>, 
              <strong>Bry-sur-Marne</strong>, <strong>Nogent-sur-Marne</strong>, 
              <strong>Le Perreux-sur-Marne</strong>, <strong>Saint-Maur-des-Fossés</strong>, 
              <strong>Créteil</strong>, <strong>Noisy-le-Grand</strong> et dans toutes les 
              communes environnantes pour vos urgences et projets de vitrerie.
            </p>

            <div className="bg-[hsl(24,95%,95%)] border-l-4 border-[hsl(24,95%,53%)] p-6 my-8 rounded-r-lg">
              <h4 className="font-bold text-[hsl(215,25%,15%)] mb-2">
                Besoin d'un vitrier à Villiers-sur-Marne ?
              </h4>
              <p className="text-muted-foreground mb-4">
                Contactez <strong>NGL SERVICES</strong> dès maintenant pour un devis gratuit ou 
                une intervention urgente. Notre équipe est disponible 24h/24 pour répondre à 
                toutes vos demandes en vitrerie.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={`tel:${companyInfo.phoneEmergency}`} className="inline-flex items-center gap-2 bg-[hsl(24,95%,53%)] text-white px-4 py-2 rounded-full font-semibold hover:bg-[hsl(24,95%,45%)] transition-colors">
                  <Phone className="w-4 h-4" />
                  {companyInfo.phoneEmergency}
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[hsl(207,78%,42%)] text-white px-4 py-2 rounded-full font-semibold hover:bg-[hsl(207,78%,35%)] transition-colors">
                  <ArrowRight className="w-4 h-4" />
                  Demander un devis
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION SEO - H2 #4 : Zones d'intervention ===== */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6 text-center">
            Zones d'intervention vitrier – Val-de-Marne et Île-de-France
          </h2>
          <p className="text-muted-foreground text-center max-w-3xl mx-auto mb-10">
            En plus de Villiers-sur-Marne (94350), notre vitrier intervient rapidement dans 
            toutes les communes du Val-de-Marne et de l'Île-de-France pour vos urgences vitrerie.
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {cities.slice(0, 14).map((city) => (
              <Link
                key={city.id}
                to={`/villes/${city.id}`}
                className={`p-3 rounded-lg text-center text-sm transition-all hover:shadow-md ${
                  city.isPrimary 
                    ? 'bg-[hsl(207,78%,42%)] text-white font-semibold' 
                    : 'bg-white hover:bg-[hsl(207,78%,95%)] text-[hsl(215,25%,15%)]'
                }`}
              >
                <span className="block">{city.name}</span>
                <span className="text-xs opacity-75">{city.postalCode}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-8 text-center">
            Avis clients – Vitrier Villiers-sur-Marne
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.id} className="bg-white">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 italic text-sm">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[hsl(207,78%,95%)] rounded-full flex items-center justify-center">
                      <span className="font-semibold text-[hsl(207,78%,42%)]">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[hsl(215,25%,15%)]">{testimonial.name}</p>
                      <p className="text-xs text-muted-foreground">{testimonial.city}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage interne pages vitrerie */}
      <section className="py-10 bg-white border-t">
        <div className="container mx-auto px-4">
          <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-6">Pages vitrerie associées :</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/vitrier-urgence-villiers-sur-marne" className="px-4 py-2 bg-[hsl(207,78%,95%)] text-[hsl(207,78%,42%)] rounded-full text-sm hover:bg-[hsl(207,78%,90%)]">
              Vitrier urgence Villiers-sur-Marne
            </Link>
            <Link to="/remplacement-vitre-cassee-villiers-sur-marne" className="px-4 py-2 bg-[hsl(207,78%,95%)] text-[hsl(207,78%,42%)] rounded-full text-sm hover:bg-[hsl(207,78%,90%)]">
              Remplacement vitre cassée 94
            </Link>
            <Link to="/bris-de-glace-villiers-sur-marne" className="px-4 py-2 bg-[hsl(207,78%,95%)] text-[hsl(207,78%,42%)] rounded-full text-sm hover:bg-[hsl(207,78%,90%)]">
              Bris de glace Villiers-sur-Marne
            </Link>
            <Link to="/services/vitrerie" className="px-4 py-2 bg-[hsl(207,78%,95%)] text-[hsl(207,78%,42%)] rounded-full text-sm hover:bg-[hsl(207,78%,90%)]">
              Services vitrerie
            </Link>
            <Link to="/renovation-fenetres-paiement-plusieurs-fois-villiers-sur-marne" className="px-4 py-2 bg-[hsl(24,95%,95%)] text-[hsl(24,95%,45%)] rounded-full text-sm hover:bg-[hsl(24,95%,90%)]">
              Rénovation fenêtres (paiement facilité)
            </Link>
            <Link to="/contact" className="px-4 py-2 bg-[hsl(24,95%,95%)] text-[hsl(24,95%,45%)] rounded-full text-sm hover:bg-[hsl(24,95%,90%)]">
              Demander un devis
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section - Schema FAQPage */}
      <FAQSection 
        faqs={faqVitrerieVilliers} 
        title="Questions fréquentes – Vitrier Villiers-sur-Marne"
        subtitle="Tout savoir sur nos services de vitrerie à Villiers-sur-Marne (94350)"
      />

      {/* Maillage interne complet */}
      <InternalLinksSection currentPage="/vitrier-villiers-sur-marne" />

      {/* CTA Final */}
      <section className="py-16 bg-[hsl(215,25%,15%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Besoin d'un vitrier à Villiers-sur-Marne ?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Vitre cassée, bris de glace, double vitrage... Contactez NGL SERVICES, 
            votre vitrier urgence 24h/24 à Villiers-sur-Marne et dans le Val-de-Marne (94).
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="emergency-btn rounded-full text-lg">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full text-lg">
                Devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VitrerieLocalPage;
